import type { IssueVisitIntakeLinkRequest, VisitIntakeLinkResponse } from "@emr/contracts";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDemoScheduling } from "../demo/DemoSchedulingProvider";
import { demoPublicBookingUrl } from "../demo/demoSchedulingClient";
import { SchedulingRequestError } from "../demo/schedulingErrors";
import { codeExpiryLabel, intakeSectionsPhrase, visitIntake } from "../pages/schedule/visitIntake";
import { PatientLinkSlip } from "./PatientLinkSlip";
import "./walk-in-intake.css";

/** The code leaves the desk screen after this long without anyone touching the dialog. */
export const WALK_IN_CODE_IDLE_MS = 3 * 60_000;

/** setTimeout's longest delay; a code is valid for a day at most, but never overflow it. */
const MAX_TIMER_MS = 2_147_483_647;

type Phase =
  | { readonly kind: "issuing" }
  | { readonly kind: "shown"; readonly issued: VisitIntakeLinkResponse }
  /** The patient opened the current code: ask before replacing it (lead decision H7). */
  | { readonly kind: "confirm" }
  | {
      readonly kind: "hidden";
      readonly reason: "idle" | "expired" | "replaced" | "kept" | "answered";
    }
  | { readonly kind: "failed"; readonly message: string };

function issueFailureMessage(error: unknown): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : "The code could not be made.";
}

/**
 * A visit's questionnaire on the patient's phone (PA-5 / EXECDEV-190): issues the visit's link
 * and shows it as a QR code with the link and when it stops working, for the patient to scan at
 * the desk. One code works at a time.
 *
 * - The link's token is in the issue response only. It lives in this component's state while the
 *   code is on screen and is dropped as soon as the code hides: when the answers arrive, after
 *   WALK_IN_CODE_IDLE_MS without anyone touching the dialog, when it expires, or when another
 *   desk replaces it (the schedule read names the live link by id). Nothing stores, logs or
 *   copies it; there is no copy button.
 * - A code the patient has opened is never replaced silently: the desk is asked first.
 * - **Print slip** prints only the slip (clinic name, QR code, link and expiry), never the
 *   patient's name, through walk-in-intake.css's print rules.
 *
 * Must sit inside DemoSchedulingProvider with a client that issues links.
 */
export function WalkInIntakeCode({
  encounterId,
  patientName,
  visitJustAdded = false,
  onDone,
  onReviewIntake,
}: {
  readonly encounterId: string;
  /** For the desk's own instructions; never on the slip. */
  readonly patientName: string;
  /** The visit was added moments ago in the same dialog (changes the failure wording). */
  readonly visitJustAdded?: boolean;
  readonly onDone: () => void;
  /** Opens the answers' review once they are in. */
  readonly onReviewIntake?: ((schedulingId: string) => void) | undefined;
}) {
  const scheduling = useDemoScheduling();
  const issueLink = scheduling.issueVisitIntakeLink;
  const refreshSchedule = scheduling.refresh;
  const intake = visitIntake(scheduling.state, encounterId);
  const timeZone = scheduling.state?.configuration.timeZone ?? "America/Toronto";
  const clinicName = scheduling.state?.configuration.clinicName ?? "Your clinic";
  const opened = intake.link?.opened === true && Date.parse(intake.link.expiresAt) > Date.now();
  const [phase, setPhase] = useState<Phase>(() =>
    intake.answered
      ? { kind: "hidden", reason: "answered" }
      : opened
        ? { kind: "confirm" }
        : { kind: "issuing" },
  );
  // A code is made when the dialog opens, unless the answers are in or the patient is using one.
  const issueOnOpen = useRef(phase.kind === "issuing");
  const mounted = useRef(true);
  const busy = useRef(false);
  const root = useRef<HTMLDivElement>(null);

  const issue = useCallback(
    async (request: IssueVisitIntakeLinkRequest) => {
      if (busy.current) return;
      if (issueLink === undefined) {
        setPhase({ kind: "failed", message: "Phone codes are not available for this clinic." });
        return;
      }
      busy.current = true;
      setPhase({ kind: "issuing" });
      try {
        const issued = await issueLink(encounterId, request);
        if (mounted.current) setPhase({ kind: "shown", issued });
      } catch (caught) {
        if (!mounted.current) return;
        if (caught instanceof SchedulingRequestError && caught.detail === "intake_link_opened") {
          // Another desk's code was opened since this screen last read the schedule.
          await refreshSchedule().catch(() => undefined);
          if (mounted.current) setPhase({ kind: "confirm" });
          return;
        }
        if (
          caught instanceof SchedulingRequestError &&
          caught.detail === "intake_already_completed"
        ) {
          await refreshSchedule().catch(() => undefined);
        }
        if (mounted.current) setPhase({ kind: "failed", message: issueFailureMessage(caught) });
      } finally {
        busy.current = false;
      }
    },
    [encounterId, issueLink, refreshSchedule],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Focus follows the content: the safe choice while asking, the code's panel otherwise.
  const keepButton = useRef<HTMLButtonElement>(null);
  const confirming = phase.kind === "confirm" && !intake.answered;
  useEffect(() => {
    (confirming ? keepButton.current : root.current)?.focus();
  }, [confirming]);

  // Issue on open, once (StrictMode runs effects twice in development).
  useEffect(() => {
    if (!issueOnOpen.current) return;
    issueOnOpen.current = false;
    void issue({});
  }, [issue]);

  const shown = phase.kind === "shown" ? phase.issued : null;
  /** Another desk issued a newer code: this one no longer works. */
  const replaced =
    shown !== null &&
    intake.link !== undefined &&
    intake.link.id !== shown.link.id &&
    Date.parse(intake.link.issuedAt) >= Date.parse(shown.link.issuedAt);
  const view: Phase = replaced ? { kind: "hidden", reason: "replaced" } : phase;

  // Drop the token as soon as the code is no longer on screen.
  useEffect(() => {
    if (shown !== null && (intake.answered || replaced)) {
      setPhase({ kind: "hidden", reason: intake.answered ? "answered" : "replaced" });
    }
  }, [intake.answered, replaced, shown]);

  // Hide after a quiet spell (restarted whenever someone uses the dialog) and when it expires.
  const idleTimer = useRef<number | undefined>(undefined);
  const restartIdle = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(
      () => setPhase({ kind: "hidden", reason: "idle" }),
      WALK_IN_CODE_IDLE_MS,
    );
  }, []);
  useEffect(() => {
    if (shown === null) return;
    restartIdle();
    const expiry = window.setTimeout(
      () => setPhase({ kind: "hidden", reason: "expired" }),
      Math.min(MAX_TIMER_MS, Math.max(0, Date.parse(shown.link.expiresAt) - Date.now())),
    );
    return () => {
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(expiry);
    };
  }, [shown, restartIdle]);
  const noteActivity = () => {
    if (shown !== null) restartIdle();
  };

  const newCode = () => {
    if (intake.link?.opened === true && Date.parse(intake.link.expiresAt) > Date.now()) {
      setPhase({ kind: "confirm" });
    } else {
      void issue({});
    }
  };

  const doneButton = (primary: boolean) => (
    <button
      type="button"
      className={`schedule-btn ${primary ? "primary" : "ghost"}`}
      onClick={onDone}
    >
      Done
    </button>
  );

  let body: ReactNode;
  let actions: ReactNode;
  if (intake.answered) {
    body = (
      <p className="walk-in-code-note" role="status">
        <strong>Answers received.</strong> {patientName}’s answers are ready to review. Nothing
        reaches the chart until someone applies it.
      </p>
    );
    actions = (
      <>
        {doneButton(onReviewIntake === undefined || intake.schedulingId === undefined)}
        {onReviewIntake !== undefined && intake.schedulingId !== undefined ? (
          <button
            type="button"
            className="schedule-btn primary"
            onClick={() => onReviewIntake(intake.schedulingId ?? "")}
          >
            Review intake
          </button>
        ) : null}
      </>
    );
  } else if (view.kind === "shown") {
    const url = demoPublicBookingUrl(view.issued.intakeQuestionnaireUrl);
    const expiry = codeExpiryLabel(view.issued.link.expiresAt, timeZone);
    const patientOpened = intake.link?.id === view.issued.link.id && intake.link.opened;
    body = (
      <>
        <p className="walk-in-code-instructions">
          Ask {patientName} to scan this with their phone camera. They confirm their date of birth,
          then answer {intakeSectionsPhrase(view.issued.questionnaireSections)} questions.
        </p>
        <PatientLinkSlip clinicName={clinicName} url={url} expiry={expiry} />
        <p className="walk-in-code-status" role="status">
          <span aria-hidden="true" />
          {patientOpened
            ? "Opened on the patient’s phone. Waiting for answers."
            : "Waiting for the patient to scan."}{" "}
          A new code replaces this one.
        </p>
      </>
    );
    actions = (
      <>
        <button type="button" className="schedule-btn ghost" onClick={() => window.print()}>
          Print slip
        </button>
        {doneButton(true)}
      </>
    );
  } else if (view.kind === "issuing") {
    body = (
      <p className="walk-in-code-note" role="status">
        Making a code…
      </p>
    );
    actions = doneButton(false);
  } else if (view.kind === "confirm") {
    const openedLink =
      intake.link?.opened === true && Date.parse(intake.link.expiresAt) > Date.now()
        ? intake.link
        : undefined;
    body = (
      <div className="walk-in-code-note" role="status">
        <p>
          <strong>
            {patientName} opened the code
            {openedLink === undefined
              ? ""
              : ` made at ${new Intl.DateTimeFormat("en-CA", {
                  timeZone,
                  hour: "numeric",
                  minute: "2-digit",
                }).format(new Date(openedLink.issuedAt))}`}{" "}
            and may be answering now.
          </strong>
        </p>
        <p>
          A new code stops the current one, and answers they have not sent yet are lost. Replace it
          only if they cannot finish on that phone.
        </p>
      </div>
    );
    actions = (
      <>
        <button
          ref={keepButton}
          type="button"
          className="schedule-btn ghost"
          data-autofocus
          onClick={() => setPhase({ kind: "hidden", reason: "kept" })}
        >
          Keep the current code
        </button>
        <button
          type="button"
          className="schedule-btn primary"
          onClick={() =>
            void issue(openedLink === undefined ? {} : { replaceOpenedLinkId: openedLink.id })
          }
        >
          Replace the code
        </button>
      </>
    );
  } else if (view.kind === "hidden") {
    const copy = {
      idle: "The code was hidden after 3 minutes without use. The patient can still use it if they scanned it.",
      expired: "This code has expired.",
      replaced: "A newer code replaced this one, so this one no longer works.",
      kept: "The current code still works. Waiting for the patient’s answers.",
      answered: "The patient’s answers are in.",
    }[view.reason];
    body = (
      <p className="walk-in-code-note" role="status">
        {copy}
      </p>
    );
    actions = (
      <>
        <button type="button" className="schedule-btn ghost" onClick={newCode}>
          Show a new code
        </button>
        {doneButton(true)}
      </>
    );
  } else {
    body = (
      <div className="schedule-modal-error" role="alert">
        <span>
          {visitJustAdded ? `${patientName} is on Today, but the code could not be made. ` : ""}
          {view.message}
        </span>
      </div>
    );
    actions = (
      <>
        <button type="button" className="schedule-btn ghost" onClick={newCode}>
          Try again
        </button>
        {doneButton(true)}
      </>
    );
  }

  return (
    // The wrapper only notices that someone is using the dialog, to keep the code up.
    // biome-ignore lint/a11y/noStaticElementInteractions: activity tracking, not an action
    <div
      ref={root}
      className="walk-in-code"
      tabIndex={-1}
      data-autofocus={confirming ? undefined : true}
      onPointerDown={noteActivity}
      onKeyDown={noteActivity}
    >
      {body}
      <div className="schedule-modal-actions">{actions}</div>
    </div>
  );
}
